(() => {
  const DESKTOP_QUERY = '(min-width: 1025px)';
  const SCROLL_DISTANCE = 5;
  const RENDER_SMOOTHING = 0.4;
  const COPY_ENTER_DURATION = 1.45;
  const COPY_EXIT_DURATION = 1.35;
  const COPY_FADE_DURATION = 1.2;
  const IMAGE_MOVE_DURATION = 2.35;
  const COPY_HOLD_DURATION = 0.18;
  const COPY_STEP_GAP = 0.16;
  const LINE_TO_IMAGE_EXTRA = 48;
  const LINE_MIN_WIDTH = 120;
  const PIN_ENTER_PROGRESS = 0.001;
  const PIN_EXIT_PROGRESS = 0.006;
  const PIN_VIEWPORT_RATIO = 0.55;
  const DEFAULT_FRAME_PATTERN = '/assets/1_[frame:5].webp';
  const DEFAULT_FRAME_MIN = 0;
  const DEFAULT_FRAME_MAX = 336;
  const FRAME_TOKEN_PATTERN = /\[frame(?::(\d+))?\]/g;
  const DEFAULT_COPY_COMPLETE_PROGRESS = [1 / 6, 2 / 6, 3 / 6, 4 / 6, 5 / 6];
  const DEFAULT_COPY_FINAL_OFFSETS = [
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ];
  const DEFAULT_COPY_LINE_EXTENSIONS = [0, 0, 0, 0, 0];
  const WHEEL_STEP_DURATION = 1.5;
  const DEFAULT_WHEEL_STEP_DURATIONS = Array.from({ length: DEFAULT_COPY_COMPLETE_PROGRESS.length + 1 }, () => WHEEL_STEP_DURATION);
  const DEFAULT_WHEEL_ENTRY_CAPTURE_RATIO = 0.2;
  const WHEEL_RELEASE_DELAY = 0.5;
  const WHEEL_ENTRY_SNAP_INPUT_WINDOW = 700;

  const ensureScrollTriggerManager = () => {
    const manager = window.SpeedianceScrollTrigger || {};
    manager._triggerIds = manager._triggerIds || new WeakMap();
    manager._triggerIdIndex = manager._triggerIdIndex || 0;
    manager.getTriggerId =
      manager.getTriggerId ||
      ((root, prefix) => {
        if (!root) return `${prefix}-scroll`;
        if (!manager._triggerIds.has(root)) {
          manager._triggerIdIndex += 1;
          const rootId = root.id ? root.id.replace(/[^a-zA-Z0-9_-]/g, '-') : manager._triggerIdIndex;
          manager._triggerIds.set(root, `${prefix}-${rootId}-${manager._triggerIdIndex}`);
        }
        return manager._triggerIds.get(root);
      });
    manager.getRefreshPriority =
      manager.getRefreshPriority ||
      ((root, offset = 0) => {
        const sections = Array.from(document.querySelectorAll('[data-gm3-motors], [data-gmu-highlights]'));
        const index = Math.max(0, sections.indexOf(root));
        return index * 10 + offset;
      });
    manager.requestRefresh =
      manager.requestRefresh ||
      ((delay = 0) => {
        if (!window.ScrollTrigger) return;

        window.clearTimeout(manager._refreshTimer);
        const run = () => {
          if (manager._refreshRaf) return;
          manager._refreshRaf = window.requestAnimationFrame(() => {
            manager._refreshRaf = null;
            window.ScrollTrigger.sort?.();
            window.ScrollTrigger.refresh();
          });
        };

        if (delay > 0) {
          manager._refreshTimer = window.setTimeout(run, delay);
        } else {
          run();
        }
      });

    window.SpeedianceScrollTrigger = manager;
    return manager;
  };

  const getPinViewportY = () => window.innerHeight * PIN_VIEWPORT_RATIO;

  const getElements = (root) => {
    const product = root.querySelector('[data-gmu-highlights-product]');
    const image = root.querySelector('.gmu-highlights__main-image');
    const fadeTargets = [
      root.querySelector('.gmu-highlights__heading'),
      ...root.querySelectorAll('.gmu-highlights__left .gmu-highlights__card'),
      ...root.querySelectorAll('.gmu-highlights__right .gmu-highlights__card'),
      root.querySelector('.gmu-highlights__card--main'),
    ].filter(Boolean);
    const copies = [
      root.querySelector('[data-gmu-highlights-copy="hero"]'),
      root.querySelector('[data-gmu-highlights-copy="built-in"]'),
      root.querySelector('[data-gmu-highlights-copy="multi-angle"]'),
      root.querySelector('[data-gmu-highlights-copy="adjustable"]'),
      root.querySelector('[data-gmu-highlights-copy="immersive"]'),
    ].filter(Boolean);
    const lines = copies
      .map((copy) => copy.querySelector('.gmu-highlights__scroll-line'))
      .filter(Boolean);

    return { product, image, fadeTargets, copies, lines };
  };

  const resetState = (root, elements) => {
    const { product, image, fadeTargets, copies, lines } = elements;

    root.classList.remove('is-scroll-active');
    root.classList.remove('is-scroll-ended');
    root.style.removeProperty('--gmu-highlights-scroll-space');
    root.style.removeProperty('--gmu-highlights-sticky-height');
    root.style.removeProperty('--gmu-highlights-hero-top');

    if (!window.gsap) {
      [product, image, ...fadeTargets, ...copies, ...lines].filter(Boolean).forEach((element) => {
        element.removeAttribute('style');
      });
      return;
    }

    window.gsap.killTweensOf([product, image].filter(Boolean));
    window.gsap.set([product, image, ...fadeTargets, ...copies, ...lines].filter(Boolean), {
      clearProps: 'all',
    });
  };

  const copyImageAttributes = (source, target) => {
    ['src', 'srcset', 'sizes', 'alt', 'width', 'height'].forEach((attribute) => {
      const value = source.getAttribute(attribute);
      if (value) target.setAttribute(attribute, value);
    });
  };

  const parseFrameRange = (value) => {
    const [minValue, maxValue] = String(value || '')
      .split('|')
      .map((part) => Number.parseInt(part?.trim(), 10));
    const min = Number.isFinite(minValue) ? minValue : DEFAULT_FRAME_MIN;
    const max = Number.isFinite(maxValue) ? maxValue : DEFAULT_FRAME_MAX;

    return {
      min: Math.min(min, max),
      max: Math.max(min, max),
    };
  };

  const getFrameConfig = (product) => {
    const range = parseFrameRange(product?.dataset.gmuFrameRange);
    const patternValue = product?.dataset.gmuFramePattern?.trim();
    const defaultPatternValue = product?.dataset.gmuDefaultFramePattern?.trim() || DEFAULT_FRAME_PATTERN;
    FRAME_TOKEN_PATTERN.lastIndex = 0;
    const pattern = FRAME_TOKEN_PATTERN.test(patternValue || '') ? patternValue : defaultPatternValue;
    FRAME_TOKEN_PATTERN.lastIndex = 0;

    return {
      pattern,
      min: range.min,
      max: range.max,
      current: null,
      loaded: new Set(),
      sources: new Map(),
      pendingFrame: null,
      pendingImages: [],
      queuedFrame: null,
      queuedImages: [],
      queuedForce: false,
      frameRaf: null,
      preloads: [],
    };
  };

  const normalizeProgressValue = (value) => {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return null;

    const normalized = parsed > 1 ? parsed / 100 : parsed;
    return window.gsap
      ? window.gsap.utils.clamp(0, 1, normalized)
      : Math.min(1, Math.max(0, normalized));
  };

  const parseCopyAnimationProgress = (value) => {
    const progress = [...DEFAULT_COPY_COMPLETE_PROGRESS];
    if (!value) return { copyCompleteProgress: progress, copyImageProgress: progress };

    value.split('|').forEach((item) => {
      const [copyValue, progressValue] = item.split(':').map((part) => part?.trim());
      const copyIndex = Number.parseInt(copyValue, 10) - 1;
      const copyProgress = normalizeProgressValue(progressValue);

      if (copyIndex < 0 || copyIndex >= progress.length || copyProgress === null) return;
      progress[copyIndex] = copyProgress;
    });

    return { copyCompleteProgress: progress, copyImageProgress: progress };
  };

  const parseCopyFinalOffsets = (value) => {
    const offsets = DEFAULT_COPY_FINAL_OFFSETS.map((offset) => ({ ...offset }));
    if (!value) return offsets;

    value.split('|').forEach((item) => {
      const [copyValue, offsetValue] = item.split(':').map((part) => part?.trim());
      const copyIndex = Number.parseInt(copyValue, 10) - 1;
      if (copyIndex < 0 || copyIndex >= offsets.length || !offsetValue) return;

      const [xValue, yValue] = offsetValue.split(',').map((part) => Number.parseFloat(part?.trim()));
      if (Number.isFinite(xValue)) offsets[copyIndex].x = xValue;
      if (Number.isFinite(yValue)) offsets[copyIndex].y = yValue;
    });

    return offsets;
  };

  const parseCopyLineExtensions = (value) => {
    const extensions = [...DEFAULT_COPY_LINE_EXTENSIONS];
    if (!value) return extensions;

    value.split('|').forEach((item) => {
      const [copyValue, extensionValue] = item.split(':').map((part) => part?.trim());
      const copyIndex = Number.parseInt(copyValue, 10) - 1;
      if (extensionValue?.toLowerCase() === 'auto') {
        if (copyIndex >= 0 && copyIndex < extensions.length) extensions[copyIndex] = 0;
        return;
      }

      const extension = Number.parseFloat(extensionValue);
      if (copyIndex < 0 || copyIndex >= extensions.length || !Number.isFinite(extension)) return;
      extensions[copyIndex] = extension;
    });

    return extensions;
  };

  const parseWheelStepDurations = (value) => {
    const durations = [...DEFAULT_WHEEL_STEP_DURATIONS];
    if (!value) return durations;

    value.split('|').forEach((item) => {
      const [stepValue, durationValue] = item.split(':').map((part) => part?.trim());
      const stepIndex = Number.parseInt(stepValue, 10) - 1;
      const duration = Number.parseFloat(durationValue);
      if (stepIndex < 0 || stepIndex >= durations.length || !Number.isFinite(duration)) return;
      durations[stepIndex] = Math.max(0.05, duration);
    });

    return durations;
  };

  const parseWheelEntryCaptureRatio = (value) => {
    const ratio = Number.parseFloat(value);
    if (!Number.isFinite(ratio)) return DEFAULT_WHEEL_ENTRY_CAPTURE_RATIO;
    return window.gsap ? window.gsap.utils.clamp(0, 1, ratio) : Math.min(1, Math.max(0, ratio));
  };

  const getStepPointsFromCopyProgress = (copyCompleteProgress) => {
    const points = [0, ...(copyCompleteProgress || DEFAULT_COPY_COMPLETE_PROGRESS), 1]
      .filter((point) => point !== null)
      .sort((a, b) => a - b)
      .filter((point, index, list) => index === 0 || Math.abs(point - list[index - 1]) > 0.001);

    if (!points.length || points[0] > 0) points.unshift(0);
    if (points[points.length - 1] < 1) points.push(1);

    return points;
  };

  const getCopyProgressAnchors = (copyCompleteTimes, copyCompleteProgress, duration) => {
    if (!duration) {
      return [
        { scroll: 0, animation: 0 },
        { scroll: 1, animation: 1 },
      ];
    }

    const anchors = [{ scroll: 0, animation: 0 }];

    copyCompleteTimes.forEach((time, index) => {
      if (!Number.isFinite(time)) return;
      anchors.push({
        scroll: copyCompleteProgress[index] ?? DEFAULT_COPY_COMPLETE_PROGRESS[index],
        animation: window.gsap.utils.clamp(0, 1, time / duration),
      });
    });
    anchors.push({ scroll: 1, animation: 1 });

    return anchors
      .sort((a, b) => a.scroll - b.scroll)
      .map((anchor, index, list) => {
        if (!index || anchor.scroll > list[index - 1].scroll) return anchor;
        return { ...anchor, scroll: Math.min(1, list[index - 1].scroll + 0.001) };
      });
  };

  const mapAnimationProgress = (progress, anchors) => {
    const points = anchors || [];
    if (!points.length) return progress;

    if (progress <= points[0].scroll) return points[0].animation;

    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const next = points[index];
      if (progress > next.scroll) continue;

      if (next.scroll === previous.scroll) return next.animation;
      const ratio = window.gsap.utils.normalize(previous.scroll, next.scroll, progress);
      return window.gsap.utils.interpolate(previous.animation, next.animation, ratio);
    }

    return points[points.length - 1].animation;
  };

  const getCopyImageProgressAnchors = (copyCompleteProgress, copyImageProgress) => {
    if (!copyImageProgress?.some((point) => point !== null)) return null;

    const anchors = [{ scroll: 0, image: 0 }];
    copyImageProgress.forEach((imageProgress, index) => {
      if (imageProgress === null) return;
      anchors.push({
        scroll: copyCompleteProgress[index] ?? DEFAULT_COPY_COMPLETE_PROGRESS[index],
        image: imageProgress,
      });
    });
    anchors.push({ scroll: 1, image: 1 });

    return anchors
      .sort((a, b) => a.scroll - b.scroll)
      .map((anchor, index, list) => {
        if (!index || anchor.scroll > list[index - 1].scroll) return anchor;
        return { ...anchor, scroll: Math.min(1, list[index - 1].scroll + 0.001) };
      });
  };

  const mapImageProgress = (progress, anchors, fallbackProgress) => {
    const points = anchors || [];
    if (!points.length) return fallbackProgress;

    if (progress <= points[0].scroll) return points[0].image;

    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const next = points[index];
      if (progress > next.scroll) continue;

      if (next.scroll === previous.scroll) return next.image;
      const ratio = window.gsap.utils.normalize(previous.scroll, next.scroll, progress);
      return window.gsap.utils.interpolate(previous.image, next.image, ratio);
    }

    return points[points.length - 1].image;
  };

  const getFrameUrl = (config, frame) => {
    const clampedFrame = window.gsap
      ? window.gsap.utils.clamp(config.min, config.max, frame)
      : Math.min(config.max, Math.max(config.min, frame));

    return config.pattern.replace(FRAME_TOKEN_PATTERN, (_token, padLength) => {
      const frameText = String(clampedFrame);
      const length = Number.parseInt(padLength, 10);
      return Number.isFinite(length) && length > 0 ? frameText.padStart(length, '0') : frameText;
    });
  };

  const setImageSource = (image, src) => {
    if (!image || image.getAttribute('src') === src) return;

    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.decoding = 'sync';
    image.setAttribute('src', src);
  };

  const applyFrame = (config, frame, images, force = false) => {
    if (!config) return;
    if (!force && config.current === frame) return;

    config.current = frame;
    const src = config.sources.get(frame) || getFrameUrl(config, frame);
    images.filter(Boolean).forEach((targetImage) => setImageSource(targetImage, src));
  };

  const queueFrame = (config, frame, images, force = false) => {
    config.queuedFrame = frame;
    config.queuedImages = images;
    config.queuedForce = config.queuedForce || force;

    if (config.frameRaf) return;
    config.frameRaf = window.requestAnimationFrame(() => {
      config.frameRaf = null;
      applyFrame(config, config.queuedFrame, config.queuedImages, config.queuedForce);
      config.queuedFrame = null;
      config.queuedImages = [];
      config.queuedForce = false;
    });
  };

  const setFrameByProgress = (config, progress, images, force = false) => {
    if (!config) return;

    const clampedProgress = window.gsap
      ? window.gsap.utils.clamp(0, 1, progress || 0)
      : Math.min(1, Math.max(0, progress || 0));
    const frame = Math.round(config.min + (config.max - config.min) * clampedProgress);

    if (!force && !config.loaded.has(frame)) {
      config.pendingFrame = frame;
      config.pendingImages = images;
      return;
    }

    queueFrame(config, frame, images, force);
  };

  const preloadFrameSequence = (config) => {
    if (!config) return;

    for (let frame = config.min; frame <= config.max; frame += 1) {
      const markLoaded = () => {
        config.loaded.add(frame);
        config.sources.set(frame, preloadImage.currentSrc || preloadImage.src);
        if (config.pendingFrame === frame) {
          applyFrame(config, frame, config.pendingImages);
          config.pendingFrame = null;
          config.pendingImages = [];
        }
      };
      const preloadImage = new Image();
      preloadImage.decoding = 'async';
      preloadImage.loading = 'eager';
      preloadImage.fetchPriority = 'high';
      preloadImage.onload = markLoaded;
      preloadImage.src = getFrameUrl(config, frame);
      if (preloadImage.complete) markLoaded();
      config.preloads.push(preloadImage);
    }
  };

  const restoreStickyCopies = (sticky) => {
    sticky?.copyPlaceholders?.forEach(({ copy, placeholder }) => {
      if (placeholder.parentNode) {
        placeholder.parentNode.insertBefore(copy, placeholder);
      }
      placeholder.remove();
    });
    if (sticky) sticky.copyPlaceholders = [];
  };

  const removeStickyStage = (sticky) => {
    restoreStickyCopies(sticky);
    sticky?.stage?.remove();
  };

  const createStickyStage = (root, image, copies) => {
    if (!image || !window.gsap) return null;

    const stage = document.createElement('div');
    const visual = document.createElement('div');
    const imageWrap = document.createElement('div');
    const visualImage = document.createElement('img');
    const copyPlaceholders = [];

    stage.className = 'gmu-highlights__sticky-stage';
    visual.className = 'gmu-highlights__sticky-visual';
    imageWrap.className = 'gmu-highlights__sticky-image-wrap';
    visualImage.className = 'gmu-highlights__sticky-image';
    visualImage.decoding = 'sync';
    visualImage.loading = 'eager';
    copyImageAttributes(image, visualImage);

    imageWrap.appendChild(visualImage);
    visual.appendChild(imageWrap);
    copies.forEach((copy) => {
      if (!copy.parentNode) return;

      const placeholder = document.createComment('gmu-highlights-copy-placeholder');
      copy.parentNode.insertBefore(placeholder, copy);
      visual.appendChild(copy);
      copyPlaceholders.push({ copy, placeholder });
    });
    stage.appendChild(visual);
    root.prepend(stage);
    window.gsap.set(visual, { autoAlpha: 0 });

    return { stage, visual, imageWrap, image: visualImage, copyPlaceholders };
  };

  const getMovingImage = (sticky) => sticky?.imageWrap || sticky?.image;

  const getCurrentTransformX = (element) => {
    if (!element || !window.gsap) return 0;

    const x = Number.parseFloat(window.gsap.getProperty(element, 'x'));
    return Number.isFinite(x) ? x : 0;
  };

  const getImageBaseRect = (sticky) => {
    const movingImage = getMovingImage(sticky);
    const imageRect = sticky?.image?.getBoundingClientRect();
    if (!movingImage || !imageRect) return null;

    const currentX = getCurrentTransformX(movingImage);
    return {
      left: imageRect.left - currentX,
      right: imageRect.right - currentX,
    };
  };

  const getImageRightX = (root, sticky) => {
    if (!root || !sticky?.visual) return 0;

    const visualRect = sticky.visual.getBoundingClientRect();
    const imageRect = getImageBaseRect(sticky) || visualRect;

    return Math.round(Math.abs(visualRect.right - imageRect.right));
  };

  const getImageLeftX = (root, sticky) => {
    if (!root || !sticky?.visual) return 0;

    const visualRect = sticky.visual.getBoundingClientRect();
    const imageRect = getImageBaseRect(sticky) || visualRect;

    return -Math.round(Math.abs(visualRect.left - imageRect.left));
  };

  const getCopyTransformX = (copy) => {
    return getCurrentTransformX(copy);
  };

  const getCopyContentRect = (copy, line, transformX = 0) => {
    if (!copy) return null;

    return Array.from(copy.children).reduce((rect, child) => {
      if (child === line) return rect;

      const childRect = child.getBoundingClientRect();
      if (!childRect.width) return rect;

      const left = childRect.left - transformX;
      const right = childRect.right - transformX;
      if (!rect) return { left, right };

      return {
        left: Math.min(rect.left, left),
        right: Math.max(rect.right, right),
      };
    }, null);
  };

  const getLineVarsToImage = (
    sticky,
    copy,
    imageX = 0,
    targetRatio = 0.18,
    useContentEdge = false,
    copyOffsetX = 0,
    lineExtension = null
  ) => {
    const line = copy?.querySelector('.gmu-highlights__scroll-line');
    if (!sticky?.visual || !copy || !line) return null;

    const transformX = getCopyTransformX(copy);
    const copyRect = copy.getBoundingClientRect();
    const visualRect = sticky.visual.getBoundingClientRect();
    const contentRect = getCopyContentRect(copy, line, transformX);
    const copyStart = copyRect.left - transformX + copyOffsetX;
    const copyEnd = copyRect.right - transformX + copyOffsetX;
    const contentStart = (useContentEdge && contentRect ? contentRect.left : copyRect.left - transformX) + copyOffsetX;
    const contentEnd = (useContentEdge && contentRect ? contentRect.right : copyRect.right - transformX) + copyOffsetX;
    const imageTarget = visualRect.left + imageX + sticky.visual.offsetWidth * targetRatio;
    const extensionRatio = Number.isFinite(lineExtension) ? lineExtension / 100 : 0;
    const productOnLeft = imageTarget < contentStart;
    const lineAnchor = productOnLeft ? copyEnd : copyStart;
    const productEdge = imageTarget + (productOnLeft ? -LINE_TO_IMAGE_EXTRA : LINE_TO_IMAGE_EXTRA);
    const baseLineWidth = Math.max(LINE_MIN_WIDTH, Math.abs(productEdge - lineAnchor));
    const adjustedLineWidth = Math.max(0, baseLineWidth * (1 + extensionRatio));
    const lineStart = productOnLeft ? lineAnchor - adjustedLineWidth : lineAnchor;
    const lineEnd = productOnLeft ? lineAnchor : lineAnchor + adjustedLineWidth;
    const lineOffset = lineStart - copyStart;
    const lineWidth = Math.max(0, lineEnd - lineStart);

    return {
      '--gmu-highlights-line-left': `${Math.round(lineOffset)}px`,
      '--gmu-highlights-line-width': `${Math.round(lineWidth)}px`,
    };
  };

  const syncStickyStage = (root, sticky, product, options = {}) => {
    if (!root || !sticky?.stage || !sticky?.visual || !product || !window.gsap) return;

    const rootRect = root.getBoundingClientRect();
    const rect = product.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const offsetTop = rect.top - rootRect.top;
    const offsetLeft = rect.left - rootRect.left;
    const stickyTop = getPinViewportY() - rect.height / 2;
    const heroTop = 5 - stickyTop;
    const scrollDistance = Math.round(window.innerHeight * SCROLL_DISTANCE);

    root.style.setProperty('--gmu-highlights-sticky-height', `${offsetTop + rect.height + scrollDistance}px`);
    root.style.setProperty('--gmu-highlights-hero-top', `${heroTop}px`);
    window.gsap.set(sticky.visual, {
      top: `${offsetTop}px`,
      left: `${offsetLeft}px`,
      marginTop: 0,
      marginLeft: 0,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    if (options.resetImage) {
      window.gsap.set(getMovingImage(sticky), { x: 0 });
    }
  };

  const showStickyVisual = (sticky, product) => {
    if (!sticky?.visual || !product || !window.gsap) return;
    if (sticky.isVisible) return;

    sticky.isVisible = true;
    window.gsap.killTweensOf([sticky.visual, product]);
    window.gsap.set(product, {
      autoAlpha: 0,
      overwrite: true,
    });
    window.gsap.set(sticky.visual, {
      autoAlpha: 1,
      overwrite: true,
    });
  };

  const hideStickyVisual = (sticky, product) => {
    if (!window.gsap) return;

    const movingImage = getMovingImage(sticky);

    if (sticky) sticky.isVisible = false;
    window.gsap.killTweensOf([sticky?.visual, movingImage, product].filter(Boolean));
    if (sticky?.visual) {
      window.gsap.set(sticky.visual, {
        autoAlpha: 0,
        overwrite: true,
      });
    }
    if (movingImage) {
      window.gsap.set(movingImage, { clearProps: 'transform' });
    }
    if (product) {
      window.gsap.set(product, {
        autoAlpha: 1,
        clearProps: 'visibility',
        overwrite: true,
      });
    }
  };

  const initHighlights = (root) => {
    root._gmuHighlightsCleanup?.();

    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    const elements = getElements(root);
    const { product, image, fadeTargets, copies, lines } = elements;

    if (!product || !window.gsap || !window.ScrollTrigger) return;

    window.gsap.registerPlugin(window.ScrollTrigger);
    const scrollManager = ensureScrollTriggerManager();

    let timeline = null;
    let scrollTrigger = null;
    let sticky = null;
    let resizeTimer = null;
    let renderTween = null;
    let stepScrollTween = null;
    let wheelReleaseTimer = null;
    let wheelStepPoints = getStepPointsFromCopyProgress(DEFAULT_COPY_COMPLETE_PROGRESS);
    let copyProgressAnchors = null;
    let copyImageProgressAnchors = null;
    let renderProgress = null;
    let scrollBehaviorRestore = null;
    let isWheelStepAnimating = false;
    let wheelStepDurations = [...DEFAULT_WHEEL_STEP_DURATIONS];
    let wheelEntryCaptureRatio = DEFAULT_WHEEL_ENTRY_CAPTURE_RATIO;
    let frameConfig = null;
    let isLeavingBack = false;
    let lastWheelInput = { direction: 0, time: 0, scrollY: 0 };

    const disableSmoothScroll = () => {
      if (scrollBehaviorRestore) return;

      const elementsToLock = [
        document.documentElement,
        document.scrollingElement,
        document.getElementById('wrapper'),
      ].filter((element, index, list) => element && list.indexOf(element) === index);

      scrollBehaviorRestore = elementsToLock.map((element) => ({
        element,
        value: element.style.scrollBehavior,
      }));
      scrollBehaviorRestore.forEach(({ element }) => {
        element.style.scrollBehavior = 'auto';
      });
    };

    const restoreSmoothScroll = () => {
      if (!scrollBehaviorRestore) return;

      scrollBehaviorRestore.forEach(({ element, value }) => {
        element.style.scrollBehavior = value;
      });
      scrollBehaviorRestore = null;
    };

    const preventWheelStep = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation?.();
    };

    const getCurrentScrollY = () => window.scrollY || window.pageYOffset || 0;

    const syncScrollTrigger = () => {
      window.ScrollTrigger.update();
    };

    const getWheelDeltaPixels = (event) => {
      if (event.deltaMode === 1) return event.deltaY * 16;
      if (event.deltaMode === 2) return event.deltaY * (window.innerHeight || 0);
      return event.deltaY;
    };

    const getNextStepIndex = (progress, direction) => {
      const offset = 0.002;

      if (direction > 0) {
        const nextIndex = wheelStepPoints.findIndex((point) => point > progress + offset);
        return nextIndex >= 0 ? nextIndex : wheelStepPoints.length - 1;
      }

      for (let index = wheelStepPoints.length - 1; index >= 0; index -= 1) {
        if (wheelStepPoints[index] < progress - offset) return index;
      }

      return 0;
    };

    const getWheelStepDuration = (targetIndex, direction) => {
      const segmentIndex = direction > 0 ? targetIndex - 1 : targetIndex;
      const clampedIndex = Math.min(wheelStepDurations.length - 1, Math.max(0, segmentIndex));
      return wheelStepDurations[clampedIndex] || WHEEL_STEP_DURATION;
    };

    const trackWheelInput = (direction) => {
      lastWheelInput = {
        direction,
        time: window.performance?.now?.() || Date.now(),
        scrollY: getCurrentScrollY(),
      };
    };

    const hasRecentWheelInput = (direction) => {
      const now = window.performance?.now?.() || Date.now();
      const hasRecentInput =
        lastWheelInput.direction === direction && now - lastWheelInput.time <= WHEEL_ENTRY_SNAP_INPUT_WINDOW;
      const hasWheelScroll = Math.abs(getCurrentScrollY() - lastWheelInput.scrollY) > 1;
      return hasRecentInput && hasWheelScroll;
    };

    const clearWheelInput = () => {
      lastWheelInput = { direction: 0, time: 0, scrollY: getCurrentScrollY() };
    };

    const snapToPinEntryStep = (direction, progress) => {
      if (!scrollTrigger || wheelStepPoints.length < 2) return;
      if (!hasRecentWheelInput(direction)) return;
      clearWheelInput();

      const currentProgress = window.gsap.utils.clamp(0, 1, progress ?? scrollTrigger.progress ?? 0);
      const targetIndex = getNextStepIndex(currentProgress, direction);
      const targetProgress = wheelStepPoints[targetIndex];
      const targetDuration = getWheelStepDuration(targetIndex, direction);

      if (targetProgress === undefined) return;
      animateToStepProgress(targetProgress, targetDuration);
    };

    const animateToStepProgress = (targetProgress, duration = WHEEL_STEP_DURATION) => {
      if (!scrollTrigger) return;
      if (isWheelStepAnimating) return;

      const progress = window.gsap.utils.clamp(0, 1, scrollTrigger.progress || 0);
      if (Math.abs(targetProgress - progress) <= 0.002) return;

      window.clearTimeout(wheelReleaseTimer);
      isWheelStepAnimating = true;
      disableSmoothScroll();
      renderTween?.kill();
      renderTween = null;
      stepScrollTween?.kill();
      const startScroll = getCurrentScrollY();
      const targetScroll = scrollTrigger.start + (scrollTrigger.end - scrollTrigger.start) * targetProgress;
      const scrollState = { value: startScroll };
      stepScrollTween = window.gsap.to(scrollState, {
        value: targetScroll,
        duration,
        ease: 'none',
        overwrite: true,
        onUpdate: () => {
          window.scrollTo(0, Math.round(scrollState.value));
          syncScrollTrigger();
        },
        onComplete: () => {
          renderProgress?.(targetProgress, true);
          stepScrollTween = null;
          wheelReleaseTimer = window.setTimeout(() => {
            isWheelStepAnimating = false;
            restoreSmoothScroll();
          }, WHEEL_RELEASE_DELAY * 1000);
        },
        onInterrupt: () => {
          stepScrollTween = null;
          isWheelStepAnimating = false;
          restoreSmoothScroll();
        },
      });
    };

    const getWheelStepProgress = (direction, event) => {
      if (!scrollTrigger) return null;
      if (scrollTrigger.isActive || isWheelStepAnimating) {
        return window.gsap.utils.clamp(0, 1, scrollTrigger.progress || 0);
      }

      const currentScroll = getCurrentScrollY();
      const wheelDelta = getWheelDeltaPixels(event);
      const projectedScroll = currentScroll + wheelDelta;
      const baseDistance = Math.min(140, window.innerHeight * 0.08);
      const entryDistance = Math.max(baseDistance, window.innerHeight * wheelEntryCaptureRatio);
      const projectedDistance = Math.min(window.innerHeight, Math.abs(wheelDelta));
      const distance = Math.max(baseDistance, projectedDistance);

      if (
        direction > 0 &&
        currentScroll < scrollTrigger.start &&
        (currentScroll >= scrollTrigger.start - entryDistance || projectedScroll >= scrollTrigger.start - distance)
      ) {
        return 0;
      }

      if (
        direction < 0 &&
        currentScroll > scrollTrigger.end &&
        (currentScroll <= scrollTrigger.end + entryDistance || projectedScroll <= scrollTrigger.end + distance)
      ) {
        return 1;
      }

      return null;
    };

    const handleWheelStep = (event) => {
      if (!mediaQuery.matches || !scrollTrigger || !wheelStepPoints.length || event.deltaY === 0) return;

      if (isWheelStepAnimating) {
        preventWheelStep(event);
        return;
      }

      const direction = event.deltaY > 0 ? 1 : -1;
      trackWheelInput(direction);
      const progress = getWheelStepProgress(direction, event);
      if (progress === null) return;

      const targetIndex = getNextStepIndex(progress, direction);
      const targetProgress = wheelStepPoints[targetIndex];
      const targetDuration = getWheelStepDuration(targetIndex, direction);
      const atStart = progress <= (wheelStepPoints[0] || 0) + 0.002;
      const atEnd = progress >= (wheelStepPoints[wheelStepPoints.length - 1] || 1) - 0.002;

      if ((direction < 0 && atStart) || (direction > 0 && atEnd)) return;

      preventWheelStep(event);
      animateToStepProgress(targetProgress, targetDuration);
    };

    const buildTimeline = () => {
      scrollTrigger?.kill();
      scrollTrigger = null;
      removeStickyStage(sticky);
      sticky = null;
      timeline?.kill();
      timeline = null;
      renderTween?.kill();
      renderTween = null;
      stepScrollTween?.kill();
      stepScrollTween = null;
      if (frameConfig?.frameRaf) {
        window.cancelAnimationFrame(frameConfig.frameRaf);
      }
      window.clearTimeout(wheelReleaseTimer);
      restoreSmoothScroll();
      isWheelStepAnimating = false;
      wheelStepDurations = [...DEFAULT_WHEEL_STEP_DURATIONS];
      wheelEntryCaptureRatio = DEFAULT_WHEEL_ENTRY_CAPTURE_RATIO;
      copyProgressAnchors = null;
      copyImageProgressAnchors = null;
      renderProgress = null;
      frameConfig = null;
      isLeavingBack = false;
      resetState(root, elements);

      if (!mediaQuery.matches) {
        scrollManager.requestRefresh();
        return;
      }

      root.classList.remove('is-scroll-active');
      root.classList.remove('is-scroll-ended');
      frameConfig = getFrameConfig(product);
      sticky = createStickyStage(root, image, copies);
      setFrameByProgress(frameConfig, 0, [image, sticky?.image], true);
      preloadFrameSequence(frameConfig);
      syncStickyStage(root, sticky, product, { resetImage: true });
      const copyFinalOffsets = parseCopyFinalOffsets(product.dataset.gmuCopyFinalOffsets);
      const copyLineExtensions = parseCopyLineExtensions(product.dataset.gmuCopyLineExtensions);
      const getCopyOffset = (index) => {
        const offset = copyFinalOffsets[index] || DEFAULT_COPY_FINAL_OFFSETS[index] || { x: 0, y: 0 };
        return {
          x: ((offset.x || 0) / 100) * (sticky?.visual?.offsetWidth || 0),
          y: ((offset.y || 0) / 100) * (sticky?.visual?.offsetHeight || 0),
        };
      };
      const getCopyLineExtension = (index) =>
        Object.prototype.hasOwnProperty.call(copyLineExtensions, index) ? copyLineExtensions[index] : null;

      window.gsap.set(product, {
        zIndex: 30,
      });
      window.gsap.set(image, {
        clearProps: 'transform',
      });
      window.gsap.set(fadeTargets, {
        autoAlpha: 1,
      });
      window.gsap.set(copies, {
        autoAlpha: 0,
      });
      window.gsap.set(copies[0], {
        x: getCopyOffset(0).x,
        y: getCopyOffset(0).y + 80,
      });
      window.gsap.set(copies[1], {
        x: getCopyOffset(1).x + 300,
        y: getCopyOffset(1).y,
      });
      window.gsap.set(copies[2], {
        x: getCopyOffset(2).x - 300,
        y: getCopyOffset(2).y,
      });
      window.gsap.set(copies[3], {
        x: getCopyOffset(3).x,
        y: getCopyOffset(3).y,
      });
      window.gsap.set(copies[4], {
        x: getCopyOffset(4).x,
        y: getCopyOffset(4).y,
      });
      window.gsap.set(lines, {
        autoAlpha: 0,
      });

      const setLineVars = (copy, vars) => {
        const line = copy?.querySelector('.gmu-highlights__scroll-line');
        if (!line || !vars) return;
        window.gsap.set(line, vars);
      };

      const getLineRevealVars = (copy) => {
        const revealFromRight = copy?.classList.contains('gmu-highlights__scroll-copy--left');

        return {
          hidden: {
            scaleX: 0,
            transformOrigin: revealFromRight ? 'right center' : 'left center',
          },
          visible: {
            scaleX: 1,
            transformOrigin: revealFromRight ? 'right center' : 'left center',
          },
        };
      };

      const setLineHidden = (copy, options = {}) => {
        const line = copy?.querySelector('.gmu-highlights__scroll-line');
        if (!line) return;
        window.gsap.set(line, {
          autoAlpha: options.autoAlpha ?? 0,
          ...getLineRevealVars(copy).hidden,
        });
      };

      const showCopy = (copy, position, options = {}) => {
        const line = copy?.querySelector('.gmu-highlights__scroll-line');
        if (!copy) return timeline;

        const enterVars = options.enterVars || { autoAlpha: 1, duration: 0.55, ease: 'none' };
        const lineRevealVars = getLineRevealVars(copy);
        const enterLineVars = {
          ...lineRevealVars.visible,
          duration: enterVars.duration,
          ease: enterVars.ease,
          ...(options.enterLineVars || {}),
        };

        timeline.to(copy, enterVars, position);
        if (line) {
          if (options.setLineVisibleBeforeEnter) {
            timeline.set(line, { autoAlpha: 1 }, position);
          } else {
            enterLineVars.autoAlpha = 1;
          }
          timeline.to(line, enterLineVars, options.enterLinePosition || '<');
        }
        timeline.to(copy, { autoAlpha: 1, duration: COPY_HOLD_DURATION });

        return timeline;
      };

      let copyLineLabelIndex = 0;
      const showCopyThenLine = (copy, position, options = {}) => {
        const line = copy?.querySelector('.gmu-highlights__scroll-line');
        if (!copy) return timeline;

        const label = `copyLineStart${copyLineLabelIndex}`;
        copyLineLabelIndex += 1;
        const enterVars = options.enterVars || { autoAlpha: 1, duration: 0.55, ease: 'none' };
        const enterDuration = enterVars.duration || 0;
        const lineRevealVars = getLineRevealVars(copy);
        const enterLineVars = {
          ...lineRevealVars.visible,
          duration: enterDuration,
          ease: enterVars.ease,
          ...(options.enterLineVars || {}),
        };

        timeline.addLabel(label, position);
        timeline.to(copy, enterVars, label);
        if (line) {
          timeline.set(line, { autoAlpha: 1 }, label);
          timeline.to(line, enterLineVars, `${label}+=${enterDuration}`);
        }
        timeline.to(copy, { autoAlpha: 1, duration: COPY_HOLD_DURATION });

        return timeline;
      };

      const hideCopy = (copy, position, options = {}) => {
        const line = copy?.querySelector('.gmu-highlights__scroll-line');
        if (!copy) return timeline;

        const leaveVars = options.leaveVars || { autoAlpha: 0, duration: 0.45, ease: 'none' };
        const lineRevealVars = getLineRevealVars(copy);
        const leaveLineVars = {
          autoAlpha: 0,
          ...lineRevealVars.hidden,
          duration: leaveVars.duration,
          ease: leaveVars.ease,
          ...(options.leaveLineVars || {}),
        };

        timeline.to(copy, leaveVars, position);
        if (line) {
          timeline.to(line, leaveLineVars, '<');
        }

        return timeline;
      };

      timeline = window.gsap.timeline({
        defaults: { ease: 'none' },
      });

      timeline.to(fadeTargets, {
        autoAlpha: 0,
        duration: 1.2,
        stagger: 0.025,
      });

      const imageRightX = getImageRightX(root, sticky);
      const imageLeftX = getImageLeftX(root, sticky);
      const movingImage = getMovingImage(sticky);
      const lineToRightImage = (imageX = imageRightX) =>
        getLineVarsToImage(
          sticky,
          copies[1],
          imageX,
          0.08,
          true,
          getCopyOffset(1).x,
          getCopyLineExtension(1)
        );
      const lineToLeftImage = (copy, index, imageX = imageLeftX) =>
        getLineVarsToImage(
          sticky,
          copy,
          imageX,
          0.82,
          true,
          getCopyOffset(index).x,
          getCopyLineExtension(index)
        );
      const { copyCompleteProgress, copyImageProgress } = parseCopyAnimationProgress(product.dataset.gmuCopyProgress);
      wheelStepDurations = parseWheelStepDurations(product.dataset.gmuWheelStepDurations);
      wheelEntryCaptureRatio = parseWheelEntryCaptureRatio(product.dataset.gmuWheelEntryCaptureRatio);
      const timelineSpacer = { value: 0 };
      const copyCompleteTimes = [];
      const imageMotion = {
        rightStart: 0,
        rightEnd: 0,
        leftStart: 0,
        leftEnd: 0,
        centerStart: 0,
        centerEnd: 0,
      };
      let copyThreeHideStart = 0;
      let copyFourHideStart = 0;
      let copyFiveHideStart = 0;

      setLineVars(
        copies[1],
        getLineVarsToImage(
          sticky,
          copies[1],
          0,
          0.08,
          true,
          getCopyOffset(1).x,
          getCopyLineExtension(1)
        )
      );
      [copies[2], copies[3], copies[4]].forEach((copy, index) => {
        setLineVars(copy, lineToLeftImage(copy, index + 2));
      });
      setLineHidden(copies[1], { autoAlpha: 1 });
      [copies[2], copies[3], copies[4]].forEach(setLineHidden);

      showCopy(copies[0], '+=0.12', {
        enterVars: {
          autoAlpha: 1,
          x: getCopyOffset(0).x,
          y: getCopyOffset(0).y,
          duration: COPY_ENTER_DURATION,
          ease: 'power2.out',
        },
      });
      copyCompleteTimes[0] = timeline.duration();
      hideCopy(copies[0], `+=${COPY_STEP_GAP}`, {
        leaveVars: { autoAlpha: 0, y: getCopyOffset(0).y - 20, duration: COPY_EXIT_DURATION, ease: 'power1.inOut' },
      });

      timeline.to(timelineSpacer, { value: '+=1', duration: COPY_STEP_GAP, ease: 'none' });
      imageMotion.rightStart = timeline.duration();
      timeline.to(timelineSpacer, { value: '+=1', duration: IMAGE_MOVE_DURATION, ease: 'none' });
      imageMotion.rightEnd = timeline.duration();
      showCopyThenLine(copies[1], imageMotion.rightStart, {
        enterVars: {
          autoAlpha: 1,
          x: getCopyOffset(1).x,
          y: getCopyOffset(1).y,
          duration: COPY_ENTER_DURATION,
          ease: 'power2.out',
        },
        enterLineVars: lineToRightImage(),
      });
      copyCompleteTimes[1] = timeline.duration();

      timeline.to(timelineSpacer, { value: '+=1', duration: COPY_STEP_GAP, ease: 'none' });
      imageMotion.leftStart = timeline.duration();
      timeline.to(timelineSpacer, { value: '+=1', duration: IMAGE_MOVE_DURATION, ease: 'none' });
      imageMotion.leftEnd = timeline.duration();
      hideCopy(copies[1], imageMotion.leftStart, {
        leaveVars: { autoAlpha: 0, y: getCopyOffset(1).y - 300, duration: COPY_EXIT_DURATION, ease: 'power1.inOut' },
        leaveLineVars: {
          autoAlpha: 1,
          ...getLineRevealVars(copies[1]).visible,
        },
      });
      showCopyThenLine(copies[2], imageMotion.leftStart + COPY_EXIT_DURATION, {
        enterVars: {
          autoAlpha: 1,
          x: getCopyOffset(2).x,
          y: getCopyOffset(2).y,
          duration: COPY_ENTER_DURATION,
          ease: 'power2.out',
        },
        enterLineVars: lineToLeftImage(copies[2], 2),
      });
      copyCompleteTimes[2] = timeline.duration();

      copyThreeHideStart = timeline.duration() + COPY_STEP_GAP;
      hideCopy(copies[2], `+=${COPY_STEP_GAP}`, {
        leaveVars: { autoAlpha: 0, duration: COPY_FADE_DURATION, ease: 'none' },
        leaveLineVars: {
          autoAlpha: 1,
          ...getLineRevealVars(copies[2]).visible,
        },
      });
      showCopyThenLine(copies[3], `+=${COPY_STEP_GAP}`, {
        enterVars: {
          autoAlpha: 1,
          x: getCopyOffset(3).x,
          y: getCopyOffset(3).y,
          duration: COPY_FADE_DURATION,
          ease: 'none',
        },
        enterLineVars: lineToLeftImage(copies[3], 3),
      });
      copyCompleteTimes[3] = timeline.duration();
      copyFourHideStart = timeline.duration() + COPY_STEP_GAP;
      hideCopy(copies[3], `+=${COPY_STEP_GAP}`, {
        leaveVars: { autoAlpha: 0, duration: COPY_FADE_DURATION, ease: 'none' },
        leaveLineVars: {
          autoAlpha: 1,
          ...getLineRevealVars(copies[3]).visible,
        },
      });
      showCopyThenLine(copies[4], `+=${COPY_STEP_GAP}`, {
        enterVars: {
          autoAlpha: 1,
          x: getCopyOffset(4).x,
          y: getCopyOffset(4).y,
          duration: COPY_FADE_DURATION,
          ease: 'none',
        },
        enterLineVars: lineToLeftImage(copies[4], 4),
      });
      copyCompleteTimes[4] = timeline.duration();
      copyFiveHideStart = timeline.duration() + COPY_STEP_GAP;
      hideCopy(copies[4], `+=${COPY_STEP_GAP}`, {
        leaveVars: { autoAlpha: 0, y: getCopyOffset(4).y - 300, duration: COPY_EXIT_DURATION, ease: 'power1.inOut' },
        leaveLineVars: {
          autoAlpha: 1,
          ...getLineRevealVars(copies[4]).visible,
        },
      });
      imageMotion.centerStart = Math.max(0, timeline.duration() - COPY_EXIT_DURATION);
      imageMotion.centerEnd = imageMotion.centerStart + IMAGE_MOVE_DURATION;
      timeline.to(timelineSpacer, {
        value: '+=1',
        duration: Math.max(0, imageMotion.centerEnd - timeline.duration()),
        ease: 'none',
      });

      timeline.pause(0);
      wheelStepPoints = getStepPointsFromCopyProgress(copyCompleteProgress);
      copyProgressAnchors = getCopyProgressAnchors(copyCompleteTimes, copyCompleteProgress, timeline.duration());
      copyImageProgressAnchors = getCopyImageProgressAnchors(copyCompleteProgress, copyImageProgress);

      const renderState = { progress: 0 };

      const renderFrameAtProgress = (progress, force = false) => {
        const clampedProgress = window.gsap.utils.clamp(0, 1, progress || 0);
        setFrameByProgress(
          frameConfig,
          mapImageProgress(clampedProgress, copyImageProgressAnchors, clampedProgress),
          [sticky?.image],
          force
        );
      };

      const renderAtProgress = (progress, frameProgress = null) => {
        const clampedProgress = window.gsap.utils.clamp(0, 1, progress || 0);
        const animationProgress = mapAnimationProgress(clampedProgress, copyProgressAnchors);
        const time = timeline.duration() * animationProgress;
        let imageX = 0;
        let copyTwoLineImageX = 0;
        let copyThreeLineImageX = 0;
        let copyFourLineImageX = 0;
        let copyFiveLineImageX = 0;

        renderState.progress = clampedProgress;
        if (frameProgress !== null) {
          renderFrameAtProgress(frameProgress);
        }
        timeline.progress(animationProgress).pause();

        if (movingImage) {
          const rightX = getImageRightX(root, sticky);
          const leftX = getImageLeftX(root, sticky);

          if (time >= imageMotion.rightStart && time < imageMotion.rightEnd) {
            const ratio = window.gsap.utils.normalize(imageMotion.rightStart, imageMotion.rightEnd, time);
            imageX = window.gsap.utils.interpolate(0, rightX, ratio);
          } else if (time >= imageMotion.rightEnd && time < imageMotion.leftStart) {
            imageX = rightX;
          } else if (time >= imageMotion.leftStart && time < imageMotion.leftEnd) {
            const ratio = window.gsap.utils.normalize(imageMotion.leftStart, imageMotion.leftEnd, time);
            imageX = window.gsap.utils.interpolate(rightX, leftX, ratio);
          } else if (time >= imageMotion.leftEnd && time < imageMotion.centerStart) {
            imageX = leftX;
          } else if (time >= imageMotion.centerStart && time < imageMotion.centerEnd) {
            const ratio = window.gsap.utils.normalize(imageMotion.centerStart, imageMotion.centerEnd, time);
            imageX = window.gsap.utils.interpolate(leftX, 0, ratio);
          }

          copyTwoLineImageX = time >= imageMotion.leftStart ? rightX : imageX;
          copyThreeLineImageX = time >= copyThreeHideStart ? leftX : imageX;
          copyFourLineImageX = time >= copyFourHideStart ? leftX : imageX;
          copyFiveLineImageX = time >= copyFiveHideStart ? leftX : imageX;
          window.gsap.set(movingImage, { x: Math.round(imageX), force3D: true });
        }

        setLineVars(
          copies[1],
          getLineVarsToImage(
            sticky,
            copies[1],
            copyTwoLineImageX,
            0.08,
            true,
            getCopyOffset(1).x,
            getCopyLineExtension(1)
          )
        );
        setLineVars(
          copies[2],
          getLineVarsToImage(
            sticky,
            copies[2],
            copyThreeLineImageX,
            0.82,
            true,
            getCopyOffset(2).x,
            getCopyLineExtension(2)
          )
        );
        setLineVars(
          copies[3],
          getLineVarsToImage(
            sticky,
            copies[3],
            copyFourLineImageX,
            0.82,
            true,
            getCopyOffset(3).x,
            getCopyLineExtension(3)
          )
        );
        setLineVars(
          copies[4],
          getLineVarsToImage(
            sticky,
            copies[4],
            copyFiveLineImageX,
            0.82,
            true,
            getCopyOffset(4).x,
            getCopyLineExtension(4)
          )
        );

        const shouldShowSticky = !isLeavingBack && (clampedProgress > 0 || scrollTrigger?.isActive);

        root.classList.toggle('is-scroll-active', shouldShowSticky && clampedProgress < 1);
        root.classList.toggle('is-scroll-ended', clampedProgress >= 1);

        if (shouldShowSticky) {
          showStickyVisual(sticky, product);
        } else {
          hideStickyVisual(sticky, product);
        }
      };

      const renderBeforePin = (force = false) => {
        if (isLeavingBack && !force) return;

        isLeavingBack = true;
        renderTween?.kill();
        renderTween = null;
        renderState.progress = 0;
        const firstFrame = frameConfig?.min ?? 0;
        window.gsap.killTweensOf([sticky?.visual, product].filter(Boolean));
        applyFrame(frameConfig, firstFrame, [image], true);
        root.classList.remove('is-scroll-active');
        root.classList.remove('is-scroll-ended');
        window.gsap.set(product, {
          autoAlpha: 1,
          clearProps: 'visibility',
          overwrite: true,
        });
        if (sticky) sticky.isVisible = false;
        if (sticky?.visual) {
          window.gsap.set(sticky.visual, {
            autoAlpha: 0,
            overwrite: true,
          });
        }
        timeline.progress(0).pause();
      };

      const renderByProgress = (progress, immediate = false) => {
        const clampedProgress = window.gsap.utils.clamp(0, 1, progress || 0);

        renderTween?.kill();
        if (immediate || isWheelStepAnimating) {
          renderAtProgress(clampedProgress, clampedProgress);
          return;
        }

        renderFrameAtProgress(clampedProgress);
        renderTween = window.gsap.to(renderState, {
          progress: clampedProgress,
          duration: RENDER_SMOOTHING,
          ease: 'power2.out',
          overwrite: true,
          onUpdate: () => renderAtProgress(renderState.progress),
          onComplete: () => renderAtProgress(clampedProgress, clampedProgress),
        });
      };
      renderProgress = renderByProgress;

      scrollTrigger = window.ScrollTrigger.create({
        id: `${scrollManager.getTriggerId(root, 'gmu-highlights')}-scroll`,
        trigger: product,
        start: `center ${PIN_VIEWPORT_RATIO * 100}%`,
        end: () => `+=${Math.round(window.innerHeight * SCROLL_DISTANCE)}`,
        pin: sticky.visual,
        pinSpacing: false,
        anticipatePin: 2,
        invalidateOnRefresh: true,
        refreshPriority: scrollManager.getRefreshPriority(root),
        onRefreshInit: () => {
          syncStickyStage(root, sticky, product);
        },
        onRefresh: (self) => {
          renderByProgress(self.progress, true);
        },
        onUpdate: (self) => {
          if (self.direction < 0 && self.progress <= PIN_EXIT_PROGRESS) {
            renderBeforePin();
            return;
          }
          if (self.direction > 0 && self.progress > 0) {
            isLeavingBack = false;
          }
          renderByProgress(self.progress);
        },
        onEnter: (self) => {
          isLeavingBack = false;
          const progress = Math.max(self.progress, PIN_ENTER_PROGRESS);
          renderByProgress(progress, true);
          snapToPinEntryStep(1, progress);
        },
        onEnterBack: (self) => {
          isLeavingBack = false;
          const progress = Math.min(self.progress || 1, 1 - PIN_ENTER_PROGRESS);
          renderByProgress(progress, true);
          snapToPinEntryStep(-1, progress);
        },
        onLeave: () => {
          renderByProgress(1, true);
        },
        onLeaveBack: () => {
          renderBeforePin(true);
        },
      });

      if (image && !image.complete) {
        image.addEventListener('load', () => scrollManager.requestRefresh(), { once: true });
      }

      scrollManager.requestRefresh();
    };

    const syncMode = () => buildTimeline();
    const syncSize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => buildTimeline(), 180);
    };

    root._gmuHighlightsCleanup = () => {
      window.clearTimeout(resizeTimer);
      scrollTrigger?.kill();
      scrollTrigger = null;
      removeStickyStage(sticky);
      sticky = null;
      timeline?.kill();
      timeline = null;
      renderTween?.kill();
      renderTween = null;
      stepScrollTween?.kill();
      stepScrollTween = null;
      window.clearTimeout(wheelReleaseTimer);
      restoreSmoothScroll();
      isWheelStepAnimating = false;
      if (frameConfig?.frameRaf) {
        window.cancelAnimationFrame(frameConfig.frameRaf);
      }
      isLeavingBack = false;
      resetState(root, elements);
      scrollManager.requestRefresh();

      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', syncMode);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(syncMode);
      }
      window.removeEventListener('resize', syncSize);
      window.removeEventListener('wheel', handleWheelStep, { capture: true });
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncMode);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(syncMode);
    }
    window.addEventListener('resize', syncSize);
    window.addEventListener('wheel', handleWheelStep, { passive: false, capture: true });

    buildTimeline();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gmu-highlights]').forEach(initHighlights);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-gmu-highlights]').forEach((root) => {
      root._gmuHighlightsCleanup?.();
    });
  });
})();
